import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const STORAGE_KEY = "cognivio_language";
const RTL_LANGUAGES = new Set(["he"]);
const NAMESPACE = "translation";

/**
 * W14 (mobile first frame) — EACH DICTIONARY IS ITS OWN CHUNK.
 *
 * The shell used to import BOTH locale files statically, so main.js carried ~483 KB of copy (≈37%
 * of the bundle) of which a reader uses exactly one language. On a 4×-throttled phone that parse
 * was the measured gap between a 0.70 s desktop first frame and a 1.32 s mobile one. Now the shell
 * fetches only the ACTIVE language's dictionary before the first render (index.js awaits
 * `i18nReady`), and the other one only when she switches.
 *
 * NO FLASH OF KEYS, BY ORDER: a switch to a language that is not loaded yet loads it FIRST and only
 * then changes the language, so no render ever runs against an empty dictionary. A switch to a
 * loaded language stays synchronous, exactly as before. A failed load leaves her in the language
 * she was reading -- the honest outcome, rather than a screen of raw keys.
 *
 * `fallbackLng` stays "en" (lib/apiErrors.js reads it), but a Hebrew reader no longer has English
 * in memory to fall back to. That changes no string: localeParity.test.js holds the en and he key
 * sets identical, so no Hebrew lookup ever needed the English fallback.
 */
export const LOCALE_LOADERS = {
  en: () => import(/* webpackChunkName: "locale-en" */ "@/locales/en/common"),
  he: () => import(/* webpackChunkName: "locale-he" */ "@/locales/he/common"),
};

const baseLanguage = (language) => String(language || "").toLowerCase().split("-")[0];
const bundleOf = (mod) => (mod && mod.default ? mod.default : mod);

/**
 * Make `instance.changeLanguage` load a missing dictionary before it switches. Returns `ensure`,
 * which loads one language's dictionary (a promise) or returns null when there is nothing to load.
 * Exported for the W14 nodes, which run it on a fresh instance with controllable loaders.
 */
export function installLazyLocales(instance, loaders) {
  const inflight = new Map();
  const ensure = (language) => {
    const base = baseLanguage(language);
    if (!loaders[base] || instance.hasResourceBundle(base, NAMESPACE)) return null;
    if (!inflight.has(base)) {
      inflight.set(
        base,
        Promise.resolve()
          .then(loaders[base])
          .then((mod) => {
            instance.addResourceBundle(base, NAMESPACE, bundleOf(mod), true, true);
          })
          .finally(() => inflight.delete(base))
      );
    }
    return inflight.get(base);
  };
  const changeLanguageNow = instance.changeLanguage.bind(instance);
  instance.changeLanguage = (language, callback) => {
    const pending = ensure(language);
    if (!pending) return changeLanguageNow(language, callback);
    return pending.then(
      () => changeLanguageNow(language, callback),
      (error) => {
        if (callback) callback(error, instance.t);
        return instance.t;
      }
    );
  };
  return ensure;
}

export function detectInitialLanguage() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && ["en", "he"].includes(stored)) {
    return stored;
  }
  const browserLanguage = (navigator.language || "en").toLowerCase();
  return browserLanguage.startsWith("he") ? "he" : "en";
}

function applyDocumentLanguage(language) {
  const lang = language || "en";
  const isRtl = RTL_LANGUAGES.has(lang);
  document.documentElement.lang = lang;
  document.documentElement.dir = isRtl ? "rtl" : "ltr";
  document.body.dataset.locale = lang;
}

// Jest cannot code-split, and every test renders real copy synchronously (TEST-I18N-1), so the
// test environment keeps both dictionaries in memory. The production build drops this branch.
const testResources =
  process.env.NODE_ENV === "test"
    ? {
        en: { [NAMESPACE]: bundleOf(require("@/locales/en/common")) },
        he: { [NAMESPACE]: bundleOf(require("@/locales/he/common")) },
      }
    : null;

const initialLanguage = detectInitialLanguage();

i18n.use(initReactI18next).init({
  resources: testResources || {},
  partialBundledLanguages: true,
  lng: initialLanguage,
  fallbackLng: "en",
  interpolation: {
    escapeValue: false,
  },
});

const ensureLanguage = installLazyLocales(i18n, LOCALE_LOADERS);

/**
 * Resolves once the active language's dictionary is in memory; index.js renders after it. If that
 * chunk cannot be fetched, English is tried before giving up, so a render is never left waiting.
 */
export const i18nReady = Promise.resolve(ensureLanguage(initialLanguage))
  .catch(() => ensureLanguage("en"))
  .catch(() => undefined);

applyDocumentLanguage(i18n.language);

i18n.on("languageChanged", (language) => {
  localStorage.setItem(STORAGE_KEY, language);
  applyDocumentLanguage(language);
});

export function isRtlLanguage(language = i18n.language) {
  return RTL_LANGUAGES.has(language);
}

export default i18n;
